import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAdmin } from "@prism/shared/constants";
import { Card, CardHeader, CardBody, DataTable, PageError, PageLoading } from "../components/index.js";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { timeAgo } from "../lib/time.js";
import { NewProposalModal } from "./AgencyProposals.jsx";
import { WorkStatusChip, fmtDate } from "./AgencyWork.jsx";

/**
 * Agency Cockpit — the staff daily driver for a single client (/clients/:id).
 * One aggregate fetch (GET /api/clients/:id/cockpit); five panels per the v1
 * spec: services/contract (stopgap-backed), data health, performance snapshot,
 * notes & knowledge, goals & targets. Impersonation is demoted to a QA tool —
 * "View as client" jumps into the existing ?clientId view-as with its banner.
 *
 * The API already scope-trims the payload for EMPLOYEEs (missing KPI sections,
 * filtered goals), so this page renders whatever sections arrive and never
 * needs to know the viewer's dataScopes.
 */

const STATUS_DOT = {
  connected: "#16a34a",
  error: "#dc2626",
  pending: "#e0a800",
  disconnected: "#a3a3a3",
};

const fmtNum = (v) => Number(v ?? 0).toLocaleString();
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// KPI tile / goal-target formatting by key. The API sends bare numbers; the
// key tells us how to render them (same convention dashboards use via
// prefix/suffix — the cockpit's shape is intentionally leaner).
const KPI_FORMAT = {
  sessions:        fmtNum,
  engaged_rate:    (v) => `${v ?? 0}%`,
  gsc_clicks:      fmtNum,
  gsc_position:    (v) => Number(v ?? 0).toFixed(1),
  ads_spend:       fmtMoney,
  ads_conversions: fmtNum,
  revenue:         fmtMoney,
};
const fmtBy = (key) => KPI_FORMAT[key] || fmtNum;

const GOAL_METRIC_OPTIONS = [
  { value: "sessions",        label: "Sessions" },
  { value: "gsc_clicks",      label: "GSC Clicks" },
  { value: "ads_spend",       label: "Ad Spend" },
  { value: "ads_conversions", label: "Ad Conversions" },
  { value: "revenue",         label: "Online Revenue" },
];
const goalMetricLabel = (m) => GOAL_METRIC_OPTIONS.find((o) => o.value === m)?.label || m;

/** Current month as "YYYY-MM" — the goals default + empty-state period. */
const currentPeriod = () => new Date().toISOString().slice(0, 7);

/** Amber ≤60d / red ≤30d renewal countdown pill (spec §10 thresholds). */
function RenewalBadge({ days }) {
  if (days == null || days > 60) return null;
  const red = days <= 30;
  const label = days < 0 ? "Contract expired" : `Renewal in ${days}d`;
  return (
    <span
      className={`inline-flex items-center h-5 px-2 rounded border text-[11px] font-medium ${
        red
          ? "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]"
          : "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]"
      }`}
    >
      {label}
    </span>
  );
}

/** Amber ≥3d / red ≥7d days-without-data warning for a health row. */
function DataAgeWarning({ days }) {
  if (days == null || days < 3) return null;
  const red = days >= 7;
  return (
    <span className={`text-[11px] font-medium ${red ? "text-[#dc2626]" : "text-[#8a4b1a]"}`}>
      No data {days}d
    </span>
  );
}

export function ClientCockpit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const api = useApiAction();
  const admin = isAdmin(user?.role);
  const superAdmin = user?.role === "SUPER_ADMIN";

  const { data, loading, error, reload } = useApi(`/api/clients/${id}/cockpit`);
  const [syncing, setSyncing] = useState(false);
  const [actionError, setActionError] = useState(null);

  async function syncNow() {
    setSyncing(true);
    setActionError(null);
    try {
      await api(`/api/sync/run?clientId=${id}`, { method: "POST" });
      await reload({ silent: true });
    } catch (e) {
      setActionError(e);
    } finally {
      setSyncing(false);
    }
  }

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading />;

  const { client, contract, health, snapshot, notes, knowledge, goals, contacts, billing, work } = data;

  return (
    <div className="max-w-[1200px] space-y-5">
      {/* Header: identity + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-9 h-9 rounded-full border border-border-default flex-shrink-0"
            style={{ backgroundColor: client.accentColor || "#e5e5e5" }}
          />
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold text-text-primary leading-tight truncate">
              {client.name}
            </h2>
            <p className="text-[12px] text-text-muted mt-0.5">
              {client.slug} · <span className="capitalize">{client.plan}</span> plan
              {client.divisionId ? <span className="capitalize"> · {client.divisionId}</span> : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={syncNow}
            disabled={syncing}
            className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
          >
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/overview?clientId=${id}`)}
            className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            View as client
          </button>
          {admin && (
            <button
              type="button"
              onClick={() => navigate(`/admin/clients/${id}`)}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity"
            >
              Manage
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="rounded-card border border-[#f3c1c1] bg-[#fef2f2] px-4 py-2.5 text-[12px] text-[#dc2626]">
          {actionError.message || "Action failed."}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ContractPanel contract={contract} clientId={id} admin={admin} navigate={navigate} clientName={client.name} billing={billing} />
        <WorkPanel work={work} clientId={id} navigate={navigate} />
        <HealthPanel sources={health?.sources || []} />
        <SnapshotPanel snapshot={snapshot} className="lg:col-span-2" />
        <NotesPanel notes={notes || []} facts={knowledge?.facts || []} clientId={id} admin={admin} navigate={navigate} />
        <GoalsPanel goals={goals || []} clientId={id} editable={superAdmin} reload={reload} />
        <ContactsPanel contacts={contacts || []} clientId={id} editable={admin} reload={reload} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Services & Contract
// ─────────────────────────────────────────────────────────────────────────

// Additive contract.status chip (4b-1, structured contracts only — the
// ClientKnowledge stopgap payload carries no status and renders no chip).
const CONTRACT_STATUS_CHIP = {
  active:   "bg-[#f0fdf4] border-[#bbe7c6] text-[#16a34a]",
  expiring: "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  expired:  "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]",
  draft:    "bg-app-bg border-border-default text-text-secondary",
  sent:     "bg-[#eff6ff] border-[#c3d9f5] text-[#2563eb]",
};

function ContractStatusChip({ status }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded border text-[11px] font-medium capitalize ${
      CONTRACT_STATUS_CHIP[status] || CONTRACT_STATUS_CHIP.draft
    }`}>
      {status}
    </span>
  );
}

// Click-to-sign line (4b-4) — a short read-only status next to the contract
// chip; staff take the actual send/countersign/mark-signed actions on the
// Contracts page, not here.
function SignatureLine({ contract }) {
  if (!contract) return null;
  if (contract.signatureStatus === "sent") {
    return <span className="text-[11px] text-text-muted ml-2">Awaiting signature</span>;
  }
  if (contract.signatureStatus === "signed") {
    return <span className="text-[11px] text-text-muted ml-2">Awaiting countersignature</span>;
  }
  if (contract.signatureStatus === "countersigned") {
    if (!contract.countersignedAt && !contract.signedAt) return null;
    return (
      <span className="text-[11px] text-text-muted ml-2">
        Executed {new Date(contract.countersignedAt || contract.signedAt).toLocaleDateString()}
      </span>
    );
  }
  return null;
}

// Billing status line (4c) — read-time unpaid/overdue summary next to the
// contract chip; the actual billing actions live on the Billing desk.
function BillingLine({ billing, clientId, admin, navigate }) {
  if (!billing) return null;
  const text = billing.unpaidCount === 0 && billing.scheduledCount === 0 ? "No billing yet"
    : billing.unpaidCount === 0 ? "Billing: current"
    : billing.overdueCount > 0 ? `Billing: $${billing.unpaid.toLocaleString()} unpaid · oldest overdue ${billing.overdueDays}d`
    : `Billing: $${billing.unpaid.toLocaleString()} invoiced, awaiting payment`;
  const cls = billing.overdueCount > 0 ? "text-negative" : "text-text-secondary";
  return (
    <div className={`text-[12px] mt-1 ${cls}`}>
      {text}
      {admin && billing.unpaidCount > 0 && <button type="button" onClick={() => navigate(`/agency/billing?tab=awaiting&clientId=${clientId}`)} className="ml-2 text-accent hover:underline">Open billing</button>}
    </div>
  );
}

function ContractPanel({ contract, clientId, admin, navigate, clientName, billing }) {
  const [proposing, setProposing] = useState(false);
  return (
    <>
    <Card className="flex flex-col">
      <CardHeader
        title="Services & Contract"
        subtitle="What we're contracted to deliver"
        right={
          <span className="inline-flex items-center gap-1.5">
            {contract && (
              <>
                <ContractStatusChip status={contract.status} />
                <SignatureLine contract={contract} />
                <BillingLine billing={billing} clientId={clientId} admin={admin} navigate={navigate} />
                <RenewalBadge days={contract.daysToRenewal} />
              </>
            )}
            {admin && (
              <button type="button" onClick={() => setProposing(true)} className="text-[12px] text-accent hover:underline ml-2">
                + New proposal
              </button>
            )}
          </span>
        }
      />
      {!contract ? (
        <CardBody className="py-8 text-center text-[13px] text-text-muted">
          No contract recorded
          {admin ? (
            <>
              {" — "}
              <button
                type="button"
                onClick={() => navigate(`/admin/clients/${clientId}?tab=knowledge`)}
                className="text-accent hover:underline font-medium"
              >
                add it in the Knowledge Base
              </button>
            </>
          ) : (
            " — ask an admin to add it in the Knowledge Base"
          )}
          .
        </CardBody>
      ) : (
        <CardBody className="space-y-3">
          {contract.services.length > 0 && (
            <ul className="space-y-1">
              {contract.services.map((s) => (
                <li key={s} className="text-[13px] text-text-primary flex items-start gap-2">
                  <span className="text-accent mt-[1px]">•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
            <div>
              <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>Start</div>
              <div className="text-text-primary mt-0.5">{contract.start || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>End</div>
              <div className="text-text-primary mt-0.5">{contract.end || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>Value</div>
              <div className="text-text-primary mt-0.5">{contract.value || "—"}</div>
            </div>
          </div>
          {contract.terms && (
            <p className="text-[12px] text-text-secondary border-t border-border-light pt-3">{contract.terms}</p>
          )}
        </CardBody>
      )}
    </Card>
    {proposing && (
      <NewProposalModal
        initial={{ clientId, title: `${clientName} — renewal / upsell` }}
        onClose={() => setProposing(false)}
        onCreated={(id) => navigate(`/agency/proposals/${id}`)}
      />
    )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Work (4d)
// ─────────────────────────────────────────────────────────────────────────

function WorkPanel({ work, clientId, navigate }) {
  const orders = work?.orders || [];
  return (
    <Card className="flex flex-col">
      <CardHeader title="Work" subtitle="Open work orders & what's due next" right={<button type="button" onClick={() => navigate(`/agency/work?clientId=${clientId}`)} className="text-[12px] text-accent hover:underline">Open board →</button>} />
      {orders.length === 0 ? <CardBody className="py-8 text-center text-[13px] text-text-muted">No open work</CardBody> : (
        <CardBody className="text-[12px] space-y-1.5">
          {work.overdueTasks > 0 && <div className="text-negative font-medium">{work.overdueTasks} overdue task{work.overdueTasks === 1 ? "" : "s"}</div>}
          {orders.map((o) => (
            <button key={o.id} type="button" onClick={() => navigate(`/agency/work?clientId=${clientId}&order=${o.id}`)} className="w-full flex items-center justify-between gap-2 text-left hover:underline">
              <span className="text-text-primary">{o.title}</span>
              <span className="flex items-center gap-2 text-text-secondary whitespace-nowrap"><WorkStatusChip status={o.status} overdue={o.overdue || o.taskCounts.overdue > 0} />{o.taskCounts.done}/{o.taskCounts.total}{o.nextTask ? ` · ${fmtDate(o.nextTask.dueDate)}` : ""}</span>
            </button>
          ))}
        </CardBody>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Data Health
// ─────────────────────────────────────────────────────────────────────────

function HealthPanel({ sources }) {
  return (
    <Card className="flex flex-col">
      <CardHeader title="Data Health" subtitle="Per-source sync status & freshness" />
      {sources.length === 0 ? (
        <CardBody className="py-8 text-center text-[13px] text-text-muted">
          No data sources connected
        </CardBody>
      ) : (
        <div className="divide-y divide-border-light">
          {sources.map((s) => (
            <div key={s.type} className="px-5 py-2.5">
              <div className="flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: STATUS_DOT[s.status] || "#a3a3a3" }}
                />
                <span className="text-[13px] font-medium text-text-primary font-mono flex-1 truncate">{s.type}</span>
                <DataAgeWarning days={s.daysSinceData} />
                <span className="text-[11px] text-text-muted w-20 text-right">{timeAgo(s.lastSync)}</span>
              </div>
              <div className="pl-5 mt-0.5 text-[11px] text-text-muted">
                {s.lastRecords != null ? `${fmtNum(s.lastRecords)} records last run` : "No sync runs yet"}
                {s.lastError && <span className="text-[#dc2626]"> · {s.lastError}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Performance Snapshot
// ─────────────────────────────────────────────────────────────────────────

function SnapshotPanel({ snapshot, className = "" }) {
  const kpis = snapshot?.kpis || [];
  return (
    <Card className={className}>
      <CardHeader
        title="Performance Snapshot"
        subtitle={`Last 28 days${snapshot?.period?.hasRealData ? "" : " · no data synced yet"}`}
      />
      <CardBody>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {kpis.map((k) => (
            <div key={k.key} className="rounded-card border border-border-default px-4 py-3">
              <div className="text-[20px] font-semibold text-text-primary" style={{ fontFeatureSettings: "'tnum'" }}>
                {fmtBy(k.key)(k.value)}
              </div>
              <div className="text-[11px] text-text-secondary mt-0.5">{k.label}</div>
              {k.goal && (
                <div className="mt-1.5 inline-flex items-center h-5 px-1.5 rounded bg-accent-bg text-[10px] text-text-secondary font-medium">
                  {fmtBy(k.key)(k.value)} / {fmtBy(k.key)(k.goal.target)} · {k.goal.pct}%
                </div>
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Notes & Knowledge
// ─────────────────────────────────────────────────────────────────────────

const FACTS_SHOWN = 8;

function NotesPanel({ notes, facts, clientId, admin, navigate }) {
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Notes & Knowledge"
        subtitle="Latest notes + quick facts"
        right={
          admin && (
            <button
              type="button"
              onClick={() => navigate(`/admin/clients/${clientId}?tab=knowledge`)}
              className="text-[12px] text-accent hover:underline font-medium"
            >
              Open Knowledge Base →
            </button>
          )
        }
      />
      {notes.length === 0 ? (
        <CardBody className="py-6 text-center text-[13px] text-text-muted">No notes yet</CardBody>
      ) : (
        <div className="divide-y divide-border-light">
          {notes.map((n) => (
            <div key={n.id} className="px-5 py-2.5">
              <p className="text-[12px] text-text-primary whitespace-pre-wrap">{n.body}</p>
              <div className="mt-1 text-[11px] text-text-muted">
                {n.authorName || "System"} · {timeAgo(n.createdAt)}
                {n.category && (
                  <span className="ml-2 inline-flex items-center h-4 px-1.5 rounded bg-app-bg border border-border-default text-[10px]">
                    {n.category}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {facts.length > 0 && (
        <div className="border-t border-border-light px-5 py-3 space-y-1.5">
          {facts.slice(0, FACTS_SHOWN).map((f) => (
            <div key={`${f.category}.${f.key}`} className="text-[12px] flex gap-2 min-w-0">
              <span className="text-text-muted flex-shrink-0">{f.key}:</span>
              <span className="text-text-secondary truncate">{f.value}</span>
            </div>
          ))}
          {facts.length > FACTS_SHOWN && (
            <div className="text-[11px] text-text-muted">+{facts.length - FACTS_SHOWN} more in the Knowledge Base</div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Contacts (4b-1.5)
// ─────────────────────────────────────────────────────────────────────────

export const CONTACT_TYPE_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "billing", label: "Billing" },
  { value: "signer",  label: "Signer" },
];

const CONTACT_TYPE_CHIP = {
  billing: "bg-[#eff6ff] border-[#c3d9f5] text-[#2563eb]",
  signer:  "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  regular: "bg-app-bg border-border-default text-text-secondary",
};

export function ContactTypeChip({ type }) {
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded border text-[11px] font-medium capitalize ${
      CONTACT_TYPE_CHIP[type] || CONTACT_TYPE_CHIP.regular
    }`}>
      {type}
    </span>
  );
}

const EMPTY_CONTACT = { name: "", title: "", email: "", phone: "", type: "regular" };

function ContactsPanel({ contacts, clientId, editable, reload }) {
  const api = useApiAction();
  const [form, setForm] = useState(null);        // null = closed; {} = adding
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload({ silent: true });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!form.name.trim()) return setError(new Error("Name is required"));
    const body = {
      name: form.name.trim(),
      type: form.type,
      // Nullable clears on update; creates just omit empties.
      ...(editingId
        ? { title: form.title.trim() || null, email: form.email.trim() || null, phone: form.phone.trim() || null }
        : {
            ...(form.title.trim() ? { title: form.title.trim() } : {}),
            ...(form.email.trim() ? { email: form.email.trim() } : {}),
            ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          }),
    };
    const ok = await run(() =>
      editingId
        ? api(`/api/clients/${clientId}/contacts/${editingId}`, { method: "PUT", body: JSON.stringify(body) })
        : api(`/api/clients/${clientId}/contacts`, { method: "POST", body: JSON.stringify(body) })
    );
    if (ok) {
      setForm(null);
      setEditingId(null);
    }
  }

  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Contacts"
        subtitle="Billing, day-to-day & signer contacts"
        right={
          editable && !form && (
            <button type="button"
              onClick={() => { setEditingId(null); setForm({ ...EMPTY_CONTACT }); setError(null); }}
              className="text-[12px] text-accent hover:underline font-medium">
              + Add Contact
            </button>
          )
        }
      />
      {contacts.length === 0 && !form ? (
        <CardBody className="py-6 text-center text-[13px] text-text-muted">No contacts recorded</CardBody>
      ) : (
        <div className="divide-y divide-border-light">
          {contacts.map((c) => (
            <div key={c.id} className="px-5 py-2.5 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-text-primary truncate">{c.name}</span>
                  <ContactTypeChip type={c.type} />
                </div>
                <div className="text-[11px] text-text-muted mt-0.5 truncate">
                  {[c.title, c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {editable && (
                <span className="inline-flex items-center gap-2.5 whitespace-nowrap pt-0.5">
                  <button type="button"
                    onClick={() => {
                      setEditingId(c.id);
                      setForm({ name: c.name ?? "", title: c.title ?? "", email: c.email ?? "", phone: c.phone ?? "", type: c.type ?? "regular" });
                      setError(null);
                    }}
                    className="text-[12px] text-accent hover:underline font-medium">Edit</button>
                  <button type="button" disabled={busy}
                    onClick={() => run(() => api(`/api/clients/${clientId}/contacts/${c.id}`, { method: "DELETE" }))}
                    className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">Delete</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {form && (
        <div className="px-5 py-3 border-t border-border-light flex flex-wrap items-center gap-2">
          <input type="text" placeholder="Name" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className={`${goalInputCls} w-36`} />
          <select value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            className={goalInputCls}>
            {CONTACT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input type="text" placeholder="Title" value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className={`${goalInputCls} w-32`} />
          <input type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={`${goalInputCls} flex-1 min-w-[140px]`} />
          <input type="text" placeholder="Phone" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className={`${goalInputCls} w-32`} />
          <button type="button" disabled={busy} onClick={save}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            {editingId ? "Save" : "Add"}
          </button>
          <button type="button" onClick={() => { setForm(null); setEditingId(null); setError(null); }}
            className="text-[12px] text-text-secondary hover:text-text-primary">
            Cancel
          </button>
        </div>
      )}
      {error && (
        <div className="px-5 pb-3 text-[11px] text-negative">{error.message || "Something went wrong."}</div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Goals & Targets
// ─────────────────────────────────────────────────────────────────────────

const goalInputCls =
  "h-8 px-2 rounded border border-border-default text-[12px] text-text-primary bg-surface focus:outline-none focus:border-accent";

function GoalsPanel({ goals, clientId, editable, reload }) {
  const api = useApiAction();
  const period = currentPeriod();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ metric: "sessions", target: "", period, note: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ target: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload({ silent: true });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addGoal() {
    const target = Number(draft.target);
    if (!(target > 0)) return setError(new Error("Target must be a positive number"));
    const ok = await run(() =>
      api(`/api/clients/${clientId}/goals`, {
        method: "POST",
        body: JSON.stringify({
          metric: draft.metric,
          target,
          period: draft.period,
          ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
        }),
      })
    );
    if (ok) {
      setAdding(false);
      setDraft({ metric: "sessions", target: "", period, note: "" });
    }
  }

  async function saveGoal(goalId) {
    const target = Number(editDraft.target);
    if (!(target > 0)) return setError(new Error("Target must be a positive number"));
    // Target-only update — the cockpit payload doesn't carry notes, so
    // round-tripping one here would silently wipe an unseen note.
    const ok = await run(() =>
      api(`/api/clients/${clientId}/goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({ target }),
      })
    );
    if (ok) setEditingId(null);
  }

  async function deleteGoal(goalId) {
    await run(() => api(`/api/clients/${clientId}/goals/${goalId}`, { method: "DELETE" }));
  }

  const columns = [
    { key: "metric", label: "Metric", render: (g) => <span className="font-medium text-text-primary">{goalMetricLabel(g.metric)}</span> },
    { key: "period", label: "Period", render: (g) => (
        <span className={g.period === period ? "" : "text-text-muted"}>
          {g.period}{g.period !== period ? " (prior)" : ""}
        </span>
      ) },
    { key: "target", label: "Target", align: "right", render: (g) =>
        editingId === g.id ? (
          <input
            type="number"
            value={editDraft.target}
            onChange={(e) => setEditDraft((d) => ({ ...d, target: e.target.value }))}
            className={`${goalInputCls} w-24 text-right`}
          />
        ) : (
          fmtBy(g.metric)(g.target)
        ) },
    { key: "actual", label: "Actual", align: "right", render: (g) => fmtBy(g.metric)(g.actual) },
    { key: "pct", label: "%", align: "right",
      colorFn: (g) => (g.pct != null && g.pct >= 100 ? "#16a34a" : undefined),
      render: (g) => (g.pct == null ? "—" : `${g.pct}%`) },
    ...(editable
      ? [{
          key: "actions", label: "", align: "right",
          render: (g) => (
            <span className="inline-flex items-center gap-2.5 justify-end whitespace-nowrap">
              {editingId === g.id ? (
                <>
                  <button type="button" disabled={busy} onClick={() => saveGoal(g.id)}
                    className="text-[12px] text-accent hover:underline font-medium disabled:opacity-50">Save</button>
                  <button type="button" onClick={() => setEditingId(null)}
                    className="text-[12px] text-text-secondary hover:text-text-primary">Cancel</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => { setEditingId(g.id); setEditDraft({ target: String(g.target) }); }}
                    className="text-[12px] text-accent hover:underline font-medium">Edit</button>
                  <button type="button" disabled={busy} onClick={() => deleteGoal(g.id)}
                    className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">Delete</button>
                </>
              )}
            </span>
          ),
        }]
      : []),
  ];

  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Goals & Targets"
        subtitle="Monthly targets vs aggregator actuals"
        right={
          editable && !adding && (
            <button type="button" onClick={() => setAdding(true)}
              className="text-[12px] text-accent hover:underline font-medium">
              + Add Goal
            </button>
          )
        }
      />
      {goals.length === 0 && !adding ? (
        <CardBody className="py-6 text-center text-[13px] text-text-muted">
          No goals set for {period}
        </CardBody>
      ) : (
        goals.length > 0 && <DataTable columns={columns} rows={goals} />
      )}
      {adding && (
        <div className="px-5 py-3 border-t border-border-light flex flex-wrap items-center gap-2">
          <select
            value={draft.metric}
            onChange={(e) => setDraft((d) => ({ ...d, metric: e.target.value }))}
            className={goalInputCls}
          >
            {GOAL_METRIC_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Target"
            value={draft.target}
            onChange={(e) => setDraft((d) => ({ ...d, target: e.target.value }))}
            className={`${goalInputCls} w-24`}
          />
          <input
            type="month"
            value={draft.period}
            onChange={(e) => setDraft((d) => ({ ...d, period: e.target.value }))}
            className={goalInputCls}
          />
          <input
            type="text"
            placeholder="Note (optional)"
            value={draft.note}
            onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            className={`${goalInputCls} flex-1 min-w-[120px]`}
          />
          <button type="button" disabled={busy} onClick={addGoal}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            Add
          </button>
          <button type="button" onClick={() => { setAdding(false); setError(null); }}
            className="text-[12px] text-text-secondary hover:text-text-primary">
            Cancel
          </button>
        </div>
      )}
      {error && (
        <div className="px-5 pb-3 text-[11px] text-negative">{error.message || "Something went wrong."}</div>
      )}
    </Card>
  );
}
